import React, { useState, useEffect } from 'react';
import { DollarSign, Users, Save, AlertCircle, Plus, Edit2, Trash2, RefreshCw, X, ToggleLeft, ToggleRight, IndianRupee, FileText } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import './BlockedPaymentsPricing.css';

// Available pages for pricing
const PAGE_OPTIONS = [
  { value: 'blocked-payments', label: 'Blocked Payments Recovery Kit' },
  { value: 'credit-control', label: 'Credit Control Solutions' },
  { value: 'prbk', label: 'PRBK Course' },
  { value: 'calling-scripts', label: 'Calling Scripts' },
  { value: 'court-cases', label: 'Court Cases' },
];

const BlockedPaymentsPricing = () => {
  const [pagePricing, setPagePricing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    page_name: '',
    page_label: '',
    price_1_friend: '',
    price_2_friends: '',
    enable_2_friends: true
  });

  useEffect(() => {
    fetchPagePricing();
  }, []);

  const fetchPagePricing = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payments/page-pricing');
      if (response.data.success) {
        setPagePricing(response.data.pagePricing);
      }
    } catch (error) {
      toast.error('Failed to fetch page pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Auto-fill page_label when page_name is selected
    if (name === 'page_name') {
      const selectedPage = PAGE_OPTIONS.find(p => p.value === value);
      if (selectedPage) {
        setFormData(prev => ({
          ...prev,
          page_label: selectedPage.label
        }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      page_name: '', 
      page_label: '', 
      price_1_friend: '', 
      price_2_friends: '', 
      enable_2_friends: true 
    });
    setShowAddForm(false);
    setEditingPricing(null);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.page_name || !formData.price_1_friend || !formData.price_2_friends) {
      toast.error('All fields are required');
      return;
    }

    try {
      const response = await api.post('/payments/page-pricing', {
        page_name: formData.page_name,
        page_label: formData.page_label,
        price_1_friend: parseFloat(formData.price_1_friend),
        price_2_friends: parseFloat(formData.price_2_friends),
        enable_2_friends: formData.enable_2_friends
      });

      if (response.data.success) {
        toast.success('Page pricing added successfully');
        fetchPagePricing();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add page pricing');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.page_name || !formData.price_1_friend || !formData.price_2_friends) {
      toast.error('All fields are required');
      return;
    }

    try {
      const response = await api.put(`/payments/page-pricing/${editingPricing.id}`, {
        page_name: formData.page_name,
        page_label: formData.page_label,
        price_1_friend: parseFloat(formData.price_1_friend),
        price_2_friends: parseFloat(formData.price_2_friends),
        enable_2_friends: formData.enable_2_friends,
        is_active: editingPricing.is_active
      });

      if (response.data.success) {
        toast.success('Page pricing updated successfully');
        fetchPagePricing();
        resetForm();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update page pricing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page pricing?')) return;

    try {
      const response = await api.delete(`/payments/page-pricing/${id}`);
      if (response.data.success) {
        toast.success('Page pricing deleted successfully');
        fetchPagePricing();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete page pricing');
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await api.patch(`/payments/page-pricing/${id}/toggle`);
      if (response.data.success) {
        toast.success('Page pricing status updated');
        fetchPagePricing();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to toggle page pricing');
    }
  };

  const startEdit = (pricing) => {
    setEditingPricing(pricing);
    setFormData({
      page_name: pricing.page_name,
      page_label: pricing.page_label,
      price_1_friend: pricing.price_1_friend.toString(),
      price_2_friends: pricing.price_2_friends.toString(),
      enable_2_friends: pricing.enable_2_friends
    });
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <RefreshCw className="spin" size={32} />
        <p>Loading page pricing...</p>
      </div>
    );
  }

  return (
    <div className="page-pricing-management">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <IndianRupee size={28} />
            Two User Pricing Management
          </h1>
          <p>Configure pricing for 1 and 2 friends across different pages</p>
        </div>
        <button 
          className="btn btn-primary btn-add-pricing"
          onClick={() => {
            resetForm();
            setShowAddForm(true);
          }}
        >
          <Plus size={18} />
          Add Page Pricing
        </button>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingPricing) && (
        <div className="card pricing-form-card">
          <div className="card-header">
            <h3>{editingPricing ? 'Edit Page Pricing' : 'Add New Page Pricing'}</h3>
            <button className="btn-close" onClick={resetForm}>
              <X size={18} />
            </button>
          </div>
          <form onSubmit={editingPricing ? handleUpdate : handleAdd}>
            <div className="form-grid">
              <div className="form-group form-group-full">
                <label>
                  <FileText size={16} />
                  Select Page *
                </label>
                <select
                  name="page_name"
                  value={formData.page_name}
                  onChange={handleInputChange}
                  className="page-select"
                  required
                  disabled={!!editingPricing}
                >
                  <option value="">Choose a page...</option>
                  {PAGE_OPTIONS.map(option => {
                    const alreadyExists = pagePricing.some(p => p.page_name === option.value && p.id !== editingPricing?.id);
                    return (
                      <option key={option.value} value={option.value} disabled={alreadyExists}>
                        {option.label} {alreadyExists ? '(Already configured)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div className="form-group">
                <label>
                  <IndianRupee size={16} />
                  Price for 1 Friend (₹) *
                </label>
                <input
                  type="number"
                  name="price_1_friend"
                  value={formData.price_1_friend}
                  onChange={handleInputChange}
                  placeholder="e.g., 4000"
                  min="100"
                  step="any"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>
                  <Users size={16} />
                  Price for 2 Friends (₹) *
                </label>
                <input
                  type="number"
                  name="price_2_friends"
                  value={formData.price_2_friends}
                  onChange={handleInputChange}
                  placeholder="e.g., 6000"
                  min="100"
                  step="any"
                  required
                />
              </div>
              
              <div className="form-group checkbox-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="enable_2_friends"
                    checked={formData.enable_2_friends}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-custom"></span>
                  Enable "For 2 Friends" option for this page
                </label>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                {editingPricing ? 'Update Pricing' : 'Add Pricing'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pricing List */}
      <div className="pricing-grid">
        {pagePricing.length === 0 ? (
          <div className="empty-state">
            <IndianRupee size={48} />
            <h3>No Page Pricing Yet</h3>
            <p>Add pricing configuration for different pages</p>
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <Plus size={18} />
              Add First Page Pricing
            </button>
          </div>
        ) : (
          pagePricing.map(pricing => (
            <div key={pricing.id} className={`pricing-card ${!pricing.is_active ? 'inactive' : ''}`}>
              <div className="pricing-header">
                <div className="pricing-status">
                  <button 
                    className={`toggle-btn ${pricing.is_active ? 'active' : ''}`}
                    onClick={() => handleToggle(pricing.id)}
                    title={pricing.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                  >
                    {pricing.is_active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                  <span className={`status-text ${pricing.is_active ? 'active' : 'inactive'}`}>
                    {pricing.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="pricing-actions">
                  <button 
                    className="btn-icon edit"
                    onClick={() => startEdit(pricing)}
                    title="Edit pricing"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button 
                    className="btn-icon delete"
                    onClick={() => handleDelete(pricing.id)}
                    title="Delete pricing"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="pricing-body">
                <h3 className="pricing-name">{pricing.page_label}</h3>
                <div className="pricing-details">
                  <div className="pricing-item">
                    <span className="pricing-label">1 Friend</span>
                    <div className="pricing-amount">
                      <IndianRupee size={16} />
                      <span>₹{parseFloat(pricing.price_1_friend).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  {pricing.enable_2_friends && (
                    <div className="pricing-item">
                      <span className="pricing-label">2 Friends</span>
                      <div className="pricing-amount">
                        <IndianRupee size={16} />
                        <span>₹{parseFloat(pricing.price_2_friends).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="pricing-savings">
                  {pricing.enable_2_friends && (
                    <span className="savings-text">
                      Save ₹{(parseFloat(pricing.price_1_friend) * 2 - parseFloat(pricing.price_2_friends)).toLocaleString('en-IN')} with 2 friends
                    </span>
                  )}
                  {!pricing.enable_2_friends && (
                    <span className="disabled-text">2 Friends option disabled</span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BlockedPaymentsPricing;
